import React, { useState } from "react";
import styles from "./Login.module.css";
import axiosInstance from "../../api/axiosConfig";
import { useNavigate, Link } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import logo from "../../assets/Images/logo.png";

const EyeIcon = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {open ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    )}
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const redirectByRole = (role) => {
    switch (role) {
      case "admin":
        return "/administrar";
      case "empresa":
        return "/panel/empresa";
      case "sede":
        return "/panel/sede";
      case "profesional":
        return "/panel";
      case "personal":
        return "/mis-consultas";
      default:
        return "/";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post("/api/login", formData);
      localStorage.setItem("token", response.data.token);

      navigate(redirectByRole(response.data.role));
    } catch (err) {
      if (err.response?.status === 403) {
        setError("El usuario está en pendiente");
      } else if (err.response?.status === 404) {
        setError("Usuario no encontrado");
      } else {
        setError("Ocurrió un error inesperado");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (response) => {
    try {
      const data = await axiosInstance.post("/api/auth/google", {
        token: response.credential,
      });
      if (data) {
        navigate(redirectByRole(data.data?.role));
      } else {
        setError("Error al autenticar el usuario");
      }
    } catch (error) {
      setError("Error al autenticar el usuario");
      console.error("Google login failed:", error);
    }
  };

  const handleGoogleError = () => {
    setError("Error en la autenticación con Google");
  };

  return (
    <div className={styles.page}>
      {/* ══════════════ PANEL IZQUIERDO ══════════════ */}
      <div className={styles.left}>
        <div className={styles.leftBg} />
        <div className={styles.leftOverlay} />

        {/* Logo */}
        <div className={styles.leftTop}>
          <Link to="/" className={styles.logo}>
            <img src={logo} alt="VitalClic" />
          </Link>
        </div>

        {/* Contenido central */}
        <div className={styles.leftMid}>
          <span className={styles.badge}>Plataforma de salud digital · Perú</span>
          <h1 className={styles.headline}>
            Tu salud,
            <br />a un <span className={styles.highlight}>clic</span>
            <br />
            de distancia.
          </h1>
          <p className={styles.sub}>
            Accedé a la red de profesionales de salud más completa del Perú. Atención presencial o
            virtual, cuando la necesitás.
          </p>
          <ul className={styles.bullets}>
            <li>Profesionales verificados y certificados</li>
            <li>Teleconsulta disponible 24 / 7</li>
            <li>Historial médico digital centralizado</li>
            <li>Agenda y pagos en un solo lugar</li>
          </ul>
        </div>

        {/* Stats + Copyright */}
        <div className={styles.leftBottom}>
          <div className={styles.stats}>
            <div className={styles.statItem}>
              <span className={styles.statValue}>2.400+</span>
              <span className={styles.statLabel}>Especialistas</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statValue}>31k</span>
              <span className={styles.statLabel}>Pacientes</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statValue}>98.4%</span>
              <span className={styles.statLabel}>Satisfacción</span>
            </div>
          </div>
          <p className={styles.copyright}>© 2026 Vitalclic. Todos los derechos reservados.</p>
        </div>
      </div>

      {/* ══════════════ PANEL DERECHO ══════════════ */}
      <div className={styles.right}>
        <div className={styles.formBox}>
          <Link to="/" className={styles.backBtn}>
            ← Inicio
          </Link>
          <p className={styles.eyebrow}>Bienvenido de vuelta</p>
          <h1>
            Iniciá sesión
            <br />
            en tu cuenta
          </h1>
          <p className={styles.formDesc}>Ingresá tus credenciales para acceder a la plataforma.</p>

          {error && <div className={styles.errorMsg}>{error}</div>}

          <form onSubmit={handleSubmit}>
            {/* Email */}
            <div className={styles.field}>
              <label className={styles.label}>Correo electrónico</label>
              <input
                className={styles.input}
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="tucorreo@email.com"
                required
              />
            </div>

            {/* Password */}
            <div className={styles.field}>
              <div className={styles.fieldHeader}>
                <label className={styles.label}>Contraseña</label>
                <Link to="/recuperar-contrasena" className={styles.forgotLink}>
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
              <div className={styles.passwordWrap}>
                <input
                  className={styles.input}
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••••"
                  required
                />
                <button
                  type="button"
                  className={styles.eyeBtn}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  <EyeIcon open={showPassword} />
                </button>
              </div>
            </div>

            {/* Recordar sesión */}
            <label className={styles.checkRow}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              Recordar mi sesión
            </label>

            {/* Submit */}
            <button type="submit" className={styles.btnSubmit} disabled={isLoading}>
              {isLoading ? (
                <CircularProgress size={18} thickness={5} sx={{ color: "#fff" }} />
              ) : (
                "INGRESAR"
              )}
            </button>
          </form>

          {/* Divider */}
          <div className={styles.divider}>o continuá con</div>

          {/* Google */}
          <GoogleOAuthProvider clientId="962025699097-20o18d8bj0tf8gj0qj3q96acrmvtr720.apps.googleusercontent.com">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              width="420"
              text="signin_with"
              shape="rectangular"
              theme="outline"
              logo_alignment="left"
            />
          </GoogleOAuthProvider>

          {/* Registro */}
          <div className={styles.registerRow}>
            ¿No tenés cuenta? <Link to="/registrar-usuario">Registrate aquí →</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
